'use client';

import { slugify } from '@/utils/helpers';
import { useEffect, useState } from 'react';
import Events from '@/utils/amplitudeEvents.json';
import type { ButtonProps } from '@zopauk/web-ui';
import { ButtonLink } from '@/components/ButtonLink';
import type { ButtonFragment } from '@/utils/dato/generated';

const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as { opera?: string } & Window).opera || '';
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  const isSmallScreen = window.innerWidth <= 768;

  const msMaxTouchPoints = (navigator as { msMaxTouchPoints?: number } & Navigator).msMaxTouchPoints || 0;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || msMaxTouchPoints > 0;

  return mobileRegex.test(userAgent.toLowerCase()) || isSmallScreen || (isTouchDevice && isSmallScreen);
};

const Button = (props: { description?: string; mobileLink?: string } & ButtonFragment) => {
  const { description = '', identifier, mobileLink, newTab, style, href, text, id } = props;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isMobileDevice());
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    window.addEventListener('orientationchange', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  const effectiveHref = isMobile && mobileLink ? mobileLink : href;
  const effectiveNewTab = isMobile && mobileLink ? false : newTab;
  const linkWithHash = effectiveHref && identifier ? `${effectiveHref}#${identifier}` : effectiveHref;

  const eventType =
    text?.trim() === 'Call our urgent support team' ? Events.TELEPHONE_CLICK_EMERGENCY_ACCOUNT : undefined;

  return href && text ? (
    <ButtonLink
      rel={effectiveNewTab ? 'nofollow noopener' : undefined}
      target={effectiveNewTab ? '_blank' : undefined}
      data-automation={`ZA.button.${slugify(text)}`}
      variant={style as ButtonProps['variant']}
      title={description || undefined}
      outline={style === 'secondary'}
      eventType={eventType}
      id={identifier || id}
      href={linkWithHash}
      className="button"
    >
      {text}
      {href.startsWith('#') ? <span className="material-symbols-outlined ml-4">expand_circle_down</span> : null}
    </ButtonLink>
  ) : null;
};

export default Button;
